import { AnimatePresence, motion } from 'framer-motion';
import { useId, useState, type ReactNode } from 'react';

/**
 * Premium expandable disclosure panel.
 *
 * Designed for the "we want depth available, but never forced" pattern:
 * summary content is always visible; deep content lives behind a single
 * subtle trigger that reveals it inline with a height + opacity tween.
 *
 * Animation is hand-tuned to feel like a documentary section unfolding,
 * not a UI accordion clicking open. No bouncy spring, no abrupt cuts.
 */
export function Disclosure({
  label,
  expandedLabel,
  children,
}: {
  /** Trigger label when collapsed (e.g. "See the full per-firm ROI model"). */
  label: string;
  /** Trigger label when expanded. Defaults to "Hide". */
  expandedLabel?: string;
  /** Deep content rendered when expanded. */
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="flex flex-col items-stretch">
      <div className="mt-8 flex justify-center">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className="group inline-flex items-center gap-3 rounded-full border border-accent-blue-border bg-accent-blue-bg px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-brand-bright transition-colors hover:bg-bg-hover"
        >
          <span>{open ? (expandedLabel ?? 'Hide') : label}</span>
          <motion.span
            aria-hidden
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="block"
          >
            <Chevron />
          </motion.span>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="panel"
            id={panelId}
            role="region"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
              opacity: { duration: 0.35, ease: 'easeOut' },
            }}
            className="overflow-hidden"
          >
            <motion.div
              initial={{ y: -8 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="pt-10"
            >
              {children}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3 w-3"
    >
      <path d="M3 4.5l3 3 3-3" />
    </svg>
  );
}
